import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ExternalLink, ArrowRight, ArrowUpRight, CheckCircle2, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

const CONTACT = 'mailto:rakshanshetty2003@gmail.com?subject=Freelance%20enquiry';
const AUTO_PLAY_INTERVAL = 5500; // 5.5 seconds

export function FreelanceShowcase({ items = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);
  const containerRef = useRef(null);

  // Mouse position for magnetic effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 200 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  // Parallax transform for the large number
  const numberX = useTransform(x, [-300, 300], [-24, 24]);
  const numberY = useTransform(y, [-300, 300], [-12, 12]);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouseX.set(e.clientX - centerX);
      mouseY.set(e.clientY - centerY);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    mouseX.set(0);
    mouseY.set(0);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const goNext = useCallback(() => {
    if (items.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const goPrev = useCallback(() => {
    if (items.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  // Auto-advance timer (pauses when user hovers or explicitly pauses)
  useEffect(() => {
    if (items.length <= 1 || isHovered || isAutoPlayPaused) return;

    const timer = setInterval(() => {
      goNext();
    }, AUTO_PLAY_INTERVAL);

    return () => clearInterval(timer);
  }, [items.length, isHovered, isAutoPlayPaused, goNext, activeIndex]);

  // Keyboard arrow navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);

  if (!items || items.length === 0) {
    return null;
  }

  const current = items[activeIndex] || items[0];
  const isPreview = current.status === 'preview';

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative w-full rounded-2xl border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] shadow-[var(--shadow-sm)] overflow-hidden p-6 sm:p-10 md:p-12 transition-colors"
    >
      {/* Top Segmented Story / Slide Progress Indicator */}
      <div className="relative z-20 mb-8 pb-5 border-b border-[var(--bg-recessed)]">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <span className="t-mono text-xs uppercase tracking-wider text-[var(--fg-secondary)] font-medium">
              Client Showcase
            </span>
            <span className="t-mono text-xs text-[var(--fg-muted)]">
              ({String(activeIndex + 1).padStart(2, '0')} of {String(items.length).padStart(2, '0')})
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="t-mono text-[11px] text-[var(--fg-muted)] hidden sm:inline">
              {isHovered ? 'Paused on hover' : 'Auto-advancing'}
            </span>
            <button
              type="button"
              onClick={() => setIsAutoPlayPaused((prev) => !prev)}
              className="p-1 rounded text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              title={isAutoPlayPaused ? 'Resume auto-play' : 'Pause auto-play'}
              aria-label={isAutoPlayPaused ? 'Resume auto-play' : 'Pause auto-play'}
            >
              {isAutoPlayPaused ? <Play size={12} /> : <Pause size={12} />}
            </button>
          </div>
        </div>

        {/* Clickable segmented slide progress bars */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
          {items.map((item, idx) => {
            const isActive = activeIndex === idx;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className="text-left py-1.5 group cursor-pointer transition-all focus-visible:outline-none"
              >
                {/* Clean Top Progress Bar Track */}
                <div className="w-full h-1 bg-[var(--bg-recessed)] rounded-full overflow-hidden mb-2">
                  {isActive && (
                    <motion.div
                      key={activeIndex}
                      className="h-full bg-[var(--accent)] origin-left"
                      initial={{ width: '0%' }}
                      animate={{ width: isHovered || isAutoPlayPaused ? '100%' : '100%' }}
                      transition={{
                        duration: isHovered || isAutoPlayPaused ? 0 : AUTO_PLAY_INTERVAL / 1000,
                        ease: 'linear',
                      }}
                    />
                  )}
                  {!isActive && idx < activeIndex && (
                    <div className="h-full w-full bg-[var(--fg-secondary)] opacity-30" />
                  )}
                </div>

                {/* Subtitle text below progress bar without any box */}
                <div className="flex items-center justify-between gap-1">
                  <span
                    className={`t-mono text-xs transition-colors truncate block ${
                      isActive
                        ? 'text-[var(--fg-primary)] font-medium'
                        : 'text-[var(--fg-secondary)] group-hover:text-[var(--fg-primary)] opacity-60 group-hover:opacity-100'
                    }`}
                  >
                    {String(idx + 1).padStart(2, '0')} {item.client}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Oversized background index number with parallax */}
      <motion.div
        className="absolute -left-6 sm:-left-12 top-1/2 -translate-y-1/2 text-[16rem] sm:text-[22rem] md:text-[28rem] font-semibold text-[var(--fg-primary)] opacity-[0.03] select-none pointer-events-none leading-none tracking-tighter"
        style={{ x: numberX, y: numberY }}
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={current.id}
            initial={{ opacity: 0, scale: 0.85, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 1.08, filter: "blur(8px)" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="block"
          >
            {String(activeIndex + 1).padStart(2, "0")}
          </motion.span>
        </AnimatePresence>
      </motion.div>

      {/* Main asymmetric layout */}
      <div className="relative z-10 flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* Left vertical rail */}
        <div className="flex lg:flex-col items-center justify-between lg:justify-center pr-0 lg:pr-8 pb-4 lg:pb-0 border-b lg:border-b-0 lg:border-r border-[var(--bg-recessed)] shrink-0">
          <motion.span
            className="t-mono text-xs text-[var(--fg-secondary)] tracking-widest uppercase lg:[writing-mode:vertical-rl] lg:[text-orientation:mixed]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            ENGAGEMENT
          </motion.span>

          {/* Progress Indicator */}
          <div className="relative h-1 w-24 lg:h-36 lg:w-px bg-[var(--bg-recessed)] lg:my-6 rounded-full overflow-hidden">
            <motion.div
              className="absolute top-0 left-0 bg-[var(--accent)] origin-top lg:w-full h-full lg:h-auto"
              animate={{
                height: `${((activeIndex + 1) / items.length) * 100}%`,
                width: `${((activeIndex + 1) / items.length) * 100}%`,
              }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>

          <span className="t-mono text-xs text-[var(--fg-secondary)]">
            {String(activeIndex + 1).padStart(2, '0')}&thinsp;/&thinsp;{String(items.length).padStart(2, '0')}
          </span>
        </div>

        {/* Center & Right Content Grid */}
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-8 items-center">
          {/* Left info column */}
          <div className="xl:col-span-7 space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs t-mono bg-[var(--bg-canvas)] border border-[var(--bg-recessed)] text-[var(--fg-secondary)] shadow-[var(--shadow-sm)]">
                {isPreview ? 'Preview Deployment' : 'Production Site'}
              </span>

              <Badge className="text-[11px] py-0.5">
                {current.sector}
              </Badge>

              <span className="t-mono text-xs text-[var(--fg-muted)] ml-auto hidden sm:inline">
                {current.domain}
              </span>
            </div>

            {/* Title with word reveal animation */}
            <div className="min-h-[64px] flex items-center">
              <AnimatePresence mode="wait">
                <motion.h3
                  key={current.id}
                  className="t-h2 text-[var(--fg-primary)] tracking-tight leading-tight"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {current.client.split(" ").map((word, i) => (
                    <motion.span
                      key={i}
                      className="inline-block mr-[0.28em]"
                      variants={{
                        hidden: { opacity: 0, y: 15 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.35,
                            delay: i * 0.04,
                            ease: [0.22, 1, 0.36, 1],
                          },
                        },
                        exit: {
                          opacity: 0,
                          y: -10,
                          transition: { duration: 0.15, delay: i * 0.02 },
                        },
                      }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.h3>
              </AnimatePresence>
            </div>

            {/* Summary */}
            <AnimatePresence mode="wait">
              <motion.p
                key={current.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
                className="text-sm sm:text-base leading-relaxed text-[var(--fg-secondary)]"
              >
                {current.summary}
              </motion.p>
            </AnimatePresence>

            {/* Key Contributions checklist if present */}
            {current.contributions && current.contributions.length > 0 && (
              <div className="pt-2">
                <p className="t-eyebrow text-[var(--fg-secondary)] mb-2.5">What was engineered & shipped</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {current.contributions.map((line) => (
                    <li
                      key={line}
                      className="flex items-start gap-2 text-xs leading-relaxed text-[var(--fg-secondary)]"
                    >
                      <CheckCircle2
                        size={13}
                        className="shrink-0 mt-0.5 text-[var(--accent)]"
                      />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions & Prominent Navigation Row */}
            <div className="pt-6 border-t border-[var(--bg-recessed)] flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2.5">
                <Button asChild variant="primary" size="sm">
                  <a href={current.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={13} className="mr-1.5" />
                    Visit Live Site
                  </a>
                </Button>

                <Button asChild variant="secondary" size="sm">
                  <a href={CONTACT}>
                    <span>Inquire for Similar</span>
                    <ArrowRight size={13} className="ml-1.5" />
                  </a>
                </Button>
              </div>

              {/* Highly Visible Prominent Navigation Controls */}
              <div className="flex items-center gap-2 p-1 rounded-xl bg-[var(--bg-canvas)] border border-[var(--bg-recessed)] shadow-[var(--shadow-sm)]">
                <button
                  type="button"
                  onClick={goPrev}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                  aria-label="Previous client"
                >
                  <ChevronLeft size={15} />
                  <span>Prev</span>
                </button>

                <div className="h-4 w-px bg-[var(--bg-recessed)]" />

                <button
                  type="button"
                  onClick={goNext}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--bg-elevated)] text-[var(--fg-primary)] border border-[var(--bg-recessed)] hover:border-[var(--accent)] shadow-[var(--shadow-sm)] transition-all cursor-pointer"
                  aria-label="Next client"
                >
                  <span>Next</span>
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>

          {/* Right browser mockup preview */}
          <div className="xl:col-span-5 relative group">
            <a
              href={current.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-[var(--bg-recessed)] bg-[var(--bg-canvas)] p-3 shadow-[var(--shadow-sm)] hover:border-[var(--accent)]/50 transition-colors cursor-pointer"
            >
              {/* Window chrome header */}
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-[var(--bg-recessed)]">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[var(--fg-muted)] opacity-30" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[var(--fg-muted)] opacity-30" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[var(--fg-muted)] opacity-30" />
                </div>
                <div className="flex-1 max-w-[200px] mx-auto px-2.5 py-0.5 rounded bg-[var(--bg-recessed)] text-center">
                  <span className="t-mono text-[10px] text-[var(--fg-secondary)] truncate block">
                    https://{current.domain}
                  </span>
                </div>
                <ArrowUpRight size={13} className="text-[var(--fg-muted)] group-hover:text-[var(--accent)] transition-colors" />
              </div>

              {/* Mockup body */}
              <div className="aspect-[16/10] rounded-lg bg-[var(--bg-recessed)] p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="t-mono text-xs text-[var(--fg-secondary)]">{current.sector}</span>
                  </div>
                  <h4 className="t-h4 text-[var(--fg-primary)] mb-2 group-hover:text-[var(--accent)] transition-colors">
                    {current.client}
                  </h4>
                  <p className="text-xs text-[var(--fg-secondary)] leading-relaxed line-clamp-3">
                    {current.summary}
                  </p>
                </div>

                <div className="pt-4 border-t border-[var(--bg-canvas)]/30 flex items-center justify-between text-xs font-medium text-[var(--fg-primary)] group-hover:text-[var(--accent)]">
                  <span>Open live site</span>
                  <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </a>

            {/* Floating edge quick navigation arrows on the mockup */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goPrev();
              }}
              className="absolute -left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-md)] flex items-center justify-center hover:bg-[var(--bg-hover)] transition-all cursor-pointer z-20"
              aria-label="Previous client"
            >
              <ChevronLeft size={15} />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goNext();
              }}
              className="absolute -right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-md)] flex items-center justify-center hover:bg-[var(--bg-hover)] transition-all cursor-pointer z-20"
              aria-label="Next client"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Subtle bottom ticker */}
      <div className="mt-10 -mb-2 overflow-hidden opacity-10 pointer-events-none select-none border-t border-[var(--bg-recessed)] pt-4">
        <motion.div
          className="flex whitespace-nowrap text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--fg-primary)]"
          animate={{ x: [0, -700] }}
          transition={{ duration: 26, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
        >
          {[...Array(5)].map((_, i) => (
            <span key={i} className="mx-4">
              {items.map((it) => it.client).join(" / ")} /
            </span>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default FreelanceShowcase;
